import { request } from "./http";

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function unwrap(payload) {
  const raw = asObject(payload);
  if (raw.item !== undefined) return raw.item;
  if (raw.items !== undefined) return raw.items;
  return payload;
}

function normalizePage(payload, fallbackPage = 0, fallbackSize = 20) {
  const raw = unwrap(payload);
  if (Array.isArray(raw)) {
    return {
      items: raw,
      page: fallbackPage,
      size: fallbackSize,
      totalElements: raw.length,
      totalPages: raw.length ? 1 : 0,
    };
  }

  const page = asObject(raw);
  const content = asArray(page.content || page.items);
  return {
    items: content,
    page: Number.isFinite(Number(page.number)) ? Number(page.number) : fallbackPage,
    size: Number.isFinite(Number(page.size)) ? Number(page.size) : fallbackSize,
    totalElements: Number.isFinite(Number(page.totalElements)) ? Number(page.totalElements) : content.length,
    totalPages: Number.isFinite(Number(page.totalPages)) ? Number(page.totalPages) : (content.length ? 1 : 0),
  };
}

function normalizeTest(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? null,
    psychologistUserId: raw.psychologistUserId ?? raw.ownerUserId ?? null,
    title: String(raw.title || "Тест без названия"),
    topic: String(raw.topic || raw.category || ""),
    estimatedMinutes: Number(raw.estimatedMinutes || raw.durationMinutes || 0) || 0,
    scope: String(raw.scope || "").toUpperCase(),
    status: String(raw.status || "").toUpperCase(),
    questionsCount: Number(raw.questionsCount || raw.questionCount || 0) || 0,
    updatedAt: raw.updatedAt || raw.lastModified || raw.createdAt || null,
  };
}

function normalizeMetrics(item = {}) {
  const raw = asObject(item);
  return {
    testId: raw.testId ?? raw.id ?? null,
    assignmentsCount: Number(raw.assignmentsCount || 0) || 0,
    assignedClientsCount: Number(raw.assignedClientsCount || 0) || 0,
    inProgressAssignmentsCount: Number(raw.inProgressAssignmentsCount || 0) || 0,
    completedAssignmentsCount: Number(raw.completedAssignmentsCount || 0) || 0,
    overdueAssignmentsCount: Number(raw.overdueAssignmentsCount || 0) || 0,
    completionRate: Number(raw.completionRate || 0) || 0,
    averageCompletedScore: raw.averageCompletedScore ?? null,
    bestCompletedScore: raw.bestCompletedScore ?? null,
  };
}

function normalizeAssignment(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? null,
    testId: raw.testId ?? null,
    testTitle: String(raw.testTitle || raw.title || "Тест"),
    clientUserId: raw.clientUserId ?? raw.userId ?? null,
    clientName: String(raw.clientName || raw.userName || "Клиент"),
    status: String(raw.status || "").toUpperCase(),
    deadlineAt: raw.deadlineAt || null,
    startedAt: raw.startedAt || null,
    completedAt: raw.completedAt || null,
    createdAt: raw.createdAt || null,
    note: String(raw.note || ""),
  };
}

export const psychologistTestsApi = {
  async listMine({ page = 0, size = 20 } = {}) {
    const payload = await request(`/api/psychologist/tests?page=${Number(page) || 0}&size=${Number(size) || 20}`);
    const normalized = normalizePage(payload, Number(page) || 0, Number(size) || 20);
    return {
      ...normalized,
      items: normalized.items.map(normalizeTest),
    };
  },

  async getMetrics(testId) {
    if (!testId) return null;
    const payload = await request(`/api/psychologist/tests/${encodeURIComponent(String(testId))}/metrics`);
    return normalizeMetrics(unwrap(payload));
  },

  async listAssignments({ page = 0, size = 20 } = {}) {
    const payload = await request(
      `/api/psychologist/tests/assignments?page=${Number(page) || 0}&size=${Number(size) || 20}`
    );
    const normalized = normalizePage(payload, Number(page) || 0, Number(size) || 20);
    return {
      ...normalized,
      items: normalized.items.map(normalizeAssignment),
    };
  },


  async create(payload = {}) {
    const res = await request(`/api/psychologist/tests`, {
      method: "POST",
      json: payload,
    });
    return normalizeTest(unwrap(res));
  },

  async update(testId, payload = {}) {
    if (!testId) throw new Error("testId is required");
    const res = await request(`/api/psychologist/tests/${encodeURIComponent(String(testId))}`, {
      method: "PUT",
      json: payload,
    });
    return normalizeTest(unwrap(res));
  },

  async publish(testId) {
    if (!testId) throw new Error("testId is required");
    const payload = await request(`/api/psychologist/tests/${encodeURIComponent(String(testId))}/publish`, {
      method: "POST",
    });
    return normalizeTest(unwrap(payload));
  },

  async assign(testId, payload = {}) {
    if (!testId) throw new Error("testId is required");
    const res = await request(`/api/psychologist/tests/${encodeURIComponent(String(testId))}/assign`, {
      method: "POST",
      json: payload,
    });
    return normalizeAssignment(unwrap(res));
  },

  async searchAll({ q = "", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    if (String(q || "").trim()) qs.set("q", String(q).trim());
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));

    const payload = await request(`/api/public/tests?${qs.toString()}`);
    const normalized = normalizePage(payload, Number(page) || 0, Number(size) || 20);
    return {
      ...normalized,
      items: normalized.items.map(normalizeTest),
    };
  },

  async listPublic({ q = "", page = 0, size = 20 } = {}) {
    const qs = new URLSearchParams();
    if (String(q || "").trim()) qs.set("q", String(q).trim());
    qs.set("page", String(Number(page) || 0));
    qs.set("size", String(Number(size) || 20));

    const payload = await request(`/api/public/psychologist-tests?${qs.toString()}`);
    const normalized = normalizePage(payload, Number(page) || 0, Number(size) || 20);
    return {
      ...normalized,
      items: normalized.items.map(normalizeTest),
    };
  },

};
